// ─────────────────────────────────────────────
//  OR Board C — the room card as the command surface.
//
//  ⚠️ THIS IS THE SECOND DESIGN. The first was modelled on an Epic screenshot:
//  three columns, a dark rail, a Gantt as the centrepiece. The user rejected it
//  as the wrong shape and the wrong look, and they were right — a reference had
//  been treated as a template, and the result was a screen from a different
//  product wearing this one's data.
//
//  This one grows out of the app's OWN board: tinted section panels, white
//  rounded room cards, navy accent, red for alarm only. There is no second
//  surface. The card the board-runner already reads fluently carries the
//  command capability, and ONE sticky rail carries the facts that belong to no
//  single room.
//
//  Every write path, guardrail and derivation from the first attempt survives
//  unchanged — none of it depended on the layout.
//
//  ⚠️ Locked to `user.activeFacilityId`. AlertProvider derives its scope from
//  the account (AlertContext.js:41) and cannot be overridden, so rendering
//  another facility would show rooms and staffing correctly while silently
//  dropping every emergency and break request. The geofence CONFIRMS this
//  facility; it never switches it.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { logout } from "../firebase/auth";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { useDayKey } from "../hooks/useDayKey";
import { useLiveConnection } from "../hooks/useLiveConnection";
import { useDeviceFacility } from "../hooks/useDeviceFacility";
import { useFacilities } from "../hooks/useFacilities";
import { useControlPanelData } from "../hooks/useControlPanelData";
import {
  assignDestinations,
  targetCaseFor,
  assignmentChangedSince,
  writesAllowed,
  WRITE_BLOCKED_COPY,
  stagedReliefRows,
  currentCrnaId,
  currentCrnaName,
} from "../domain/panelAssign";
import { nextBoundary } from "../domain/controlPanel";
import { placeProvider } from "../features/control-panel/placeProvider";
import { setRosterMemberDeparted, updateRoomStatus } from "../firebase/firestore";
import { updateAssignmentRow } from "../firebase/firestore";
import { confirmRelief } from "../features/relief/confirmRelief";
import { roomForOrRoom } from "../domain/gridTemplate";
import { groupRoomsByLocation } from "../domain/locations";
import { facilityLabel } from "../domain/facilities";
import NavMenu from "../components/NavMenu";
import SignOutButton from "../components/SignOutButton";
import BoardSwitcher from "../components/controlPanel/BoardSwitcher";
import PanelStatusRail from "../components/controlPanel/PanelStatusRail";
import PanelRoomCard from "../components/controlPanel/PanelRoomCard";
import PanelSheet from "../components/controlPanel/PanelSheet";
import FacilityPicker from "../components/controlPanel/FacilityPicker";
import BenchSections from "../components/controlPanel/BenchSections";
import BreakSheetHost from "../components/controlPanel/BreakSheetHost";
import PanelTimesSheet from "../components/controlPanel/PanelTimesSheet";
import styles, { sectionTint, C } from "../components/controlPanel/panelCard.styles";

const NOW_TICK_MS = 30_000;

function useNowMinutes() {
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return nowMin;
}

export default function BoardCPage() {
  const { user, isAdmin, isAIC, isDoctor } = useAuth();
  const nowMin = useNowMinutes();
  const dateKey = useDayKey();
  const facilities = useFacilities();

  const device = useDeviceFacility({
    facilities,
    accountFacilityId: user?.activeFacilityId || null,
    allowedFacilityIds: user?.facilityIds,
    isAdmin,
  });
  const facilityId = device.facilityId;

  const breakWindows = useMemo(
    () => (facilities || []).find((f) => f && f.id === facilityId)?.breakWindows || null,
    [facilities, facilityId]
  );

  const {
    model, lanes, breakRowByRoomId, alarms, railMetrics, loading,
    assignments, rooms, available, locations, occupiedRooms,
    departedMembers, travelingMembers,
  } = useControlPanelData(facilityId, dateKey, { nowMin, breakWindows });

  const connection = useLiveConnection(facilityId);
  const { roster } = useDailyRoster(facilityId, dateKey);

  const isWallDisplay = user?.role === "boardwall";
  const canAct = Boolean(!isWallDisplay && (isAdmin || isAIC || isDoctor));

  // One sheet, two entry points. `sheetLane` is room-first (the card's Swap),
  // `sheetPerson` is person-first (kept for a bench-row entry later).
  const [sheetPerson, setSheetPerson] = useState(null);
  const [sheetLane, setSheetLane] = useState(null);
  const [breakLane, setBreakLane] = useState(null);
  const [timesLane, setTimesLane] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const destinations = useMemo(
    () => assignDestinations(lanes, sheetPerson?.id),
    [lanes, sheetPerson]
  );

  const reliefRows = useMemo(
    () =>
      stagedReliefRows(assignments, {
        nowMin,
        resolveRoom: (orRoom) => roomForOrRoom(orRoom, rooms),
      }),
    [assignments, rooms, nowMin]
  );
  const reliefByRoomId = useMemo(() => {
    const m = new Map();
    for (const r of reliefRows) if (r.roomId) m.set(r.roomId, r);
    return m;
  }, [reliefRows]);

  const guard = useCallback(() => {
    const gate = writesAllowed({ canAct, connectionBand: connection.band });
    if (!gate.allowed) {
      setError(WRITE_BLOCKED_COPY[gate.reason] || "That is not allowed right now.");
      return false;
    }
    return true;
  }, [canAct, connection.band]);

  const commitPlacement = useCallback(
    async ({ person, lane: destLane, displaced, disposition, travelDest, travelDestName }) => {
      if (!guard()) return;
      // Re-read from the LIVE model, never the snapshot the sheet was built
      // with: rows carry no version, so two people acting at once is silent
      // last-write-wins.
      const liveLane = lanes.find((l) => l.roomId === destLane.roomId) || null;
      const liveCase = targetCaseFor(liveLane);
      const conflict = assignmentChangedSince({ ahpId: displaced ? displaced.id : null }, liveCase);
      if (conflict) { setError(conflict); return; }

      setBusy(true);
      try {
        await placeProvider({
          // Room-first supplies the person; person-first already has one.
          person: person !== undefined ? person : sheetPerson,
          lane: liveLane || destLane,
          targetCase: liveCase,
          displaced, disposition, travelDest, travelDestName,
          facilityId, dateKey, actorUid: user?.uid,
        });
        setSheetPerson(null);
        setSheetLane(null);
        setError(null);
      } catch (e) {
        setError(e?.message || "That change could not be saved.");
      } finally { setBusy(false); }
    },
    [guard, lanes, sheetPerson, facilityId, dateKey, user]
  );

  const onConfirmRelief = useCallback(
    async (row) => {
      if (!guard()) return;
      setBusy(true);
      try {
        await confirmRelief(row.assignment, { roomId: row.roomId });
        setError(null);
      } catch (e) {
        setError(e?.message || "That relief could not be confirmed.");
      } finally { setBusy(false); }
    },
    [guard]
  );

  const commitTimes = useCallback(
    async ({ startTime, endTime }) => {
      if (!guard()) return;
      const target = targetCaseFor(timesLane);
      if (!target?.id) { setError("That room has no case to reschedule."); return; }
      setBusy(true);
      try {
        await updateAssignmentRow(target.id, { startTime, endTime });
        setTimesLane(null);
        setError(null);
      } catch (e) {
        setError(e?.message || "Those times could not be saved.");
      } finally { setBusy(false); }
    },
    [guard, timesLane]
  );

  const clock = useMemo(() => {
    const h = Math.floor(nowMin / 60);
    const m = nowMin % 60;
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  }, [nowMin]);

  // Send the person in a room home for the day. Marking them departed is what
  // takes them out of the staffing maths at every later boundary, so it is a
  // real change and not a note.
  const onSendHome = useCallback(
    async (lane) => {
      if (!guard()) return;
      const who = currentCrnaId(lane);
      if (!who) { setError(`${lane.roomName} has nobody to send home.`); return; }
      setBusy(true);
      try {
        await setRosterMemberDeparted(facilityId, dateKey, who, true, user?.uid);
        setError(null);
      } catch (e) { setError(e?.message || "That person could not be sent home."); }
      finally { setBusy(false); }
    },
    [guard, facilityId, dateKey, user]
  );

  const onStatus = useCallback(
    async (lane, status) => {
      if (!guard()) return;
      setBusy(true);
      try { await updateRoomStatus(lane.roomId, status); setError(null); }
      catch (e) { setError(e?.message || "That status could not be saved."); }
      finally { setBusy(false); }
    },
    [guard]
  );

  const boundary = useMemo(
    () => nextBoundary(model.staffing, nowMin, model.doctorStaffing),
    [model.staffing, model.doctorStaffing, nowMin]
  );

  const nextOutGroup = useMemo(() => {
    const groups = railMetrics.shiftGroups || [];
    return groups.find((g) => g.hour != null && g.hour * 60 > nowMin) || null;
  }, [railMetrics.shiftGroups, nowMin]);

  // ⚠️ Organised like the BOARD, not like the timeline model.
  //
  // `buildTimeline` keeps only rooms with a MATCHED CASE; the board shows every
  // room whose status is non-empty OR that is scheduled today. Those differ, so
  // deriving sections from the model would silently drop rooms the board
  // displays — and a command surface that shows fewer rooms than the board it
  // replaces is worse than the board.
  const sections = useMemo(
    () => groupRoomsByLocation(occupiedRooms || [], locations || []),
    [occupiedRooms, locations]
  );
  const laneByRoomId = useMemo(() => {
    const m = new Map();
    for (const l of lanes) m.set(l.roomId, l);
    return m;
  }, [lanes]);

  const label = facilityLabel(facilityId, facilities);

  // Fail closed: no fix, denied permission, no fences, two overlapping radii, a
  // stored choice the geofence disagrees with, or a facility this account
  // cannot open all land here rather than rendering somebody else's board.
  if (!facilityId) {
    return (
      <div style={styles.page}>
        <FacilityPicker
          reason={device.reason}
          candidates={device.candidates}
          onChoose={device.choose}
          onRetry={device.retry}
          onSignOut={logout}
          busy={device.checking}
        />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: C.card, borderBottom: `1px solid ${C.line}` }}>
        <NavMenu />
        <span style={{ fontWeight: 800, color: C.inkStrong }}>OR Board C</span>
        <BoardSwitcher compact />
        {/* marginLeft:auto — see BoardAPage. */}
        <span style={{ marginLeft: "auto" }}><SignOutButton /></span>
      </div>

      <PanelStatusRail
        facilityName={label}
        dateKey={dateKey}
        clock={clock}
        connection={connection}
        counts={{
          emergencies: alarms.emergencyRoomIds?.size || 0,
          uncovered: alarms.uncoveredRoomIds?.size || 0,
          breaksWaiting: alarms.breakRequestRoomIds?.size || 0,
        }}
        boundary={boundary}
        metrics={{
          roomsRunning: railMetrics.roomsRunning,
          roomsTotal: railMetrics.roomsTotal,
          onShift: railMetrics.onShift,
          bench: railMetrics.bench,
          nextOut: nextOutGroup ? nextOutGroup.label : null,
        }}
        readOnly={!canAct}
        locationLine={device.verification === "verified" ? "location verified" : "set on this device"}
      />

      {/* The only element entitled to interrupt: it says the SCREEN cannot be
          trusted, which supersedes anything happening in the ORs. */}
      {connection.band ? (
        <div role="alert" style={{ background: C.alarmBg, borderBottom: `2px solid ${C.alarm}`, color: "#7f1d1d", fontWeight: 800, padding: "8px 16px" }}>
          ▲ {connection.band}
        </div>
      ) : null}

      {error ? (
        <div style={{ background: C.warnBg, borderBottom: `1px solid ${C.warnLine}`, color: C.warn, fontWeight: 700, padding: "8px 16px", display: "flex", gap: 12 }}>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} style={{ ...styles.act, marginLeft: "auto", minHeight: 26 }}>
            Dismiss
          </button>
        </div>
      ) : null}

      {sheetPerson || sheetLane ? (
        <div style={{ margin: "12px 16px", maxWidth: 520 }}>
          <PanelSheet
            person={sheetPerson}
            lane={sheetLane}
            bench={available?.providers || []}
            destinations={destinations}
            facilities={facilities}
            busy={busy}
            error={null}
            onCommit={commitPlacement}
            onClose={() => { setSheetPerson(null); setSheetLane(null); }}
          />
        </div>
      ) : null}

      {breakLane ? (
        <div style={{ margin: "12px 16px", maxWidth: 460 }}>
          <BreakSheetHost
            room={breakLane.room}
            assignment={targetCaseFor(breakLane.lane)}
            providerName={targetCaseFor(breakLane.lane)?.label || ""}
            lane={breakLane.lane}
            breakRow={breakRowByRoomId.get(breakLane.lane.roomId)}
            // Only people genuinely free can cover: someone already in a room
            // cannot be in two places, and the board derives the bench as
            // "rostered, not departed, not in a room".
            candidates={available?.providers || []}
            onDone={() => setBreakLane(null)}
            onError={(m) => { setBreakLane(null); setError(m); }}
          />
        </div>
      ) : null}

      {timesLane ? (
        <div style={{ margin: "12px 16px", maxWidth: 460 }}>
          <PanelTimesSheet
            lane={timesLane}
            caseItem={targetCaseFor(timesLane)}
            otherCases={(timesLane.cases || []).slice(0, -1)}
            departMin={targetCaseFor(timesLane)?.departMin ?? null}
            busy={busy}
            onCommit={commitTimes}
            onClose={() => setTimesLane(null)}
          />
        </div>
      ) : null}

      {loading ? (
        <p style={{ padding: "24px 16px", color: C.muted }}>Loading the day…</p>
      ) : lanes.length === 0 ? (
        <p style={{ padding: "24px 16px", color: C.muted }}>No rooms running for this date.</p>
      ) : (
        sections.map((sec) => {
          if (!sec.rooms.length) return null;
          return (
            <section key={sec.key} style={{ ...styles.section, background: sectionTint(sec.label) }}>
              <h2 style={styles.sectionHead}>
                {sec.label}
                <span style={styles.countChip}>{sec.rooms.length}</span>
              </h2>
              <div style={styles.grid}>
                {sec.rooms.map((room) => {
                  // A room with no matched case still gets a card: the board
                  // shows it, so the panel shows it.
                  const lane = laneByRoomId.get(room.id) || {
                    roomId: room.id,
                    roomName: room.name,
                    sectionLabel: sec.label,
                    cases: [],
                  };
                  return (
                  <PanelRoomCard
                    key={room.id}
                    lane={lane}
                    room={room}
                    axisStart={model.axisStart}
                    axisEnd={model.axisEnd}
                    nowMin={nowMin}
                    alarms={alarms}
                    breakRow={breakRowByRoomId.get(lane.roomId)}
                    breakWindows={breakWindows}
                    reliefRow={reliefByRoomId.get(lane.roomId)}
                    canAct={canAct}
                    onAssign={() =>
                      setSheetLane({
                        roomId: lane.roomId,
                        roomName: lane.roomName,
                        holderId: currentCrnaId(lane),
                        holderName: currentCrnaName(lane),
                      })
                    }
                    onEditTimes={() => setTimesLane(lane)}
                    onRelieve={(l) => setError(`Staging relief for ${l.roomName} is next; Confirm already works when one is staged.`)}
                    onSendHome={(l) => onSendHome(l)}
                    onMessage={() => window.dispatchEvent(new CustomEvent("atc-open-messages"))}
                    onStatus={(l, st) => onStatus(l, st)}
                    onMoveRoom={(l) =>
                      setSheetLane({
                        roomId: l.roomId, roomName: l.roomName,
                        holderId: currentCrnaId(l), holderName: currentCrnaName(l),
                      })
                    }
                    onConfirmRelief={onConfirmRelief}
                    onCoverBreak={() => setBreakLane({ lane, room })}
                  />
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {/* The board renders these as SECTIONS, not as counts in a header. A
          person who is off a room and in none of them is invisible, which is
          the exact failure the placement flow exists to prevent. */}
      <BenchSections
        bench={available?.providers || []}
        doctors={available?.doctors || []}
        departed={departedMembers}
        traveling={travelingMembers}
        canAct={canAct}
        onAssign={(person) => setSheetPerson(person)}
      />

      {!loading && !roster?.crnas?.length ? (
        <p style={{ padding: "0 16px 24px", color: C.muted }}>
          No roster for this date. Departure times are unknown, not absent.
        </p>
      ) : null}
    </div>
  );
}
